import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Trophy, 
  Ruler, 
  Weight, 
  MapPin, 
  CheckCircle,
  Users2,
  Award,
  TrendingUp,
  Mail,
  Sparkles,
  X
} from 'lucide-react';
import type { ESPNAthlete } from '@/hooks/useESPNAthletes';
import { AthleteBuzzTab } from './AthleteBuzzTab';
import { EditableBioSection } from './EditableBioSection';
import { InviteToPlatformModal } from './InviteToPlatformModal';
import { useTalentBio } from '@/hooks/useTalentBio';
import { getLeagueLogo, getLeagueColor } from '@/utils/leagueLogos';
import { getTeamColors } from '@/utils/teamColors';


interface MarketplaceAthleteDetailModalProps {
  athlete: ESPNAthlete | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInviteToDeal?: (athlete: ESPNAthlete) => void;
}

export const MarketplaceAthleteDetailModal: React.FC<MarketplaceAthleteDetailModalProps> = ({
  athlete,
  open,
  onOpenChange,
  onInviteToDeal,
}) => {
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const bioHook = useTalentBio({
    talentType: 'athlete',
    talentId: athlete?.id || '',
    name: athlete?.display_name || athlete?.name || '',
    sport: athlete?.sport,
    position: athlete?.position || undefined,
    team: athlete?.current_team_name || undefined,
    league: athlete?.league || undefined,
    stats: athlete?.current_season_stats as Record<string, unknown> || undefined,
    existingBio: athlete?.bio,
  });

  const leagueLogo = getLeagueLogo(athlete?.league, athlete?.sport);
  const leagueColor = getLeagueColor(athlete?.league, athlete?.sport);

  if (!athlete) return null;

  const getClaimStatusBadge = () => {
    switch (athlete.claim_status) {
      case 'approved':
        return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Claimed</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">Pending</Badge>;
      default:
        return <Badge variant="outline">Unclaimed</Badge>;
    }
  };

  const fromLocation = [athlete.birthplace_city, athlete.birthplace_state, athlete.birthplace_country]
    .filter(Boolean)
    .join(', ');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] p-0 overflow-hidden [&>button]:hidden flex flex-col">
        <ScrollArea className="flex-1 overflow-auto">
          {/* Header */}
          <div className="relative px-6 pt-6 pb-4">
            {/* Top row: claim badge + close */}
            <div className="flex items-center justify-between mb-4">
              {getClaimStatusBadge()}
              <button onClick={() => onOpenChange(false)} className="text-muted-foreground hover:text-foreground transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Avatar + League Logo + Team Logo row */}
            <div className="flex items-center justify-center gap-4 mb-4">
              <Avatar className="h-16 w-16 border-2 border-border shadow-lg">
                <AvatarImage src={athlete.headshot_url || ''} className="object-cover" />
                <AvatarFallback className="text-lg">
                  {athlete.first_name?.[0] || athlete.name[0]}
                </AvatarFallback>
              </Avatar>
              {leagueLogo && (
                <div className="h-14 w-14 rounded-full flex items-center justify-center shadow-lg" style={{ backgroundColor: leagueColor }}>
                  <img src={leagueLogo} alt="" className="h-9 w-9 object-contain" />
                </div>
              )}
              {athlete.current_team_logo && (
                <div className="h-14 w-14 rounded-full flex items-center justify-center shadow-md" style={{ backgroundColor: getTeamColors(athlete.current_team_name || '').primary }}>
                  <img src={athlete.current_team_logo} alt={athlete.current_team_name || ''} className="h-9 w-9 object-contain" />
                </div>
              )}
            </div>

            {/* Name centered */}
            <h2 className="text-2xl font-bold text-center">
              {athlete.display_name || athlete.name}
            </h2>
            <p className="text-center text-muted-foreground mt-1">
              {athlete.position && `${athlete.position} • `}
              {athlete.current_team_name || athlete.league || athlete.sport.toUpperCase()}
            </p>
            {athlete.is_verified && (
              <div className="flex items-center justify-center mt-2">
                <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Verified
                </Badge>
              </div>
            )}
          </div>

          {/* Tabs */}
          <Tabs defaultValue="overview" className="px-6 pb-6">
            <TabsList className="w-full grid grid-cols-4">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="athletic">Athletic</TabsTrigger>
              <TabsTrigger value="stats">Stats</TabsTrigger>
              <TabsTrigger value="buzz" className="flex items-center gap-1">
                <Sparkles className="h-3 w-3" />
                Buzz
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-4 space-y-4">
              {/* Quick info grid */}
              <div className="grid grid-cols-2 gap-y-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Team</p>
                  <p className="font-bold">{athlete.current_team_name || '—'}{athlete.jersey_number ? ` #${athlete.jersey_number}` : ''}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Position</p>
                  <p className="font-bold">{athlete.position || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Age</p>
                  <p className="font-bold">{athlete.age ? `${athlete.age} years old` : '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">From</p>
                  <p className="font-bold">{fromLocation || '—'}</p>
                </div>
              </div>

              <EditableBioSection
                bio={bioHook.bio}
                isEdited={bioHook.isEdited}
                generatedBio={bioHook.generatedBio}
                isLoading={bioHook.isLoading}
                isGenerating={bioHook.isGenerating}
                isSaving={bioHook.isSaving}
                onGenerate={bioHook.generateBio}
                onSave={bioHook.saveBio}
                onReset={bioHook.resetToGenerated}
              />
            </TabsContent>

            <TabsContent value="athletic" className="mt-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {athlete.height && (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                    <div className="bg-primary/10 rounded-lg p-2">
                      <Ruler className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Height</p>
                      <p className="font-bold">{athlete.height}</p>
                    </div>
                  </div>
                )}
                {athlete.weight && (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                    <div className="bg-primary/10 rounded-lg p-2">
                      <Weight className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Weight</p>
                      <p className="font-bold">{athlete.weight}</p>
                    </div>
                  </div>
                )}
                {athlete.years_experience && (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                    <div className="bg-primary/10 rounded-lg p-2">
                      <TrendingUp className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Experience</p>
                      <p className="font-bold">{athlete.years_experience} {athlete.years_experience === 1 ? 'year' : 'years'}</p>
                    </div>
                  </div>
                )}
                {athlete.college && (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                    <div className="bg-primary/10 rounded-lg p-2">
                      <MapPin className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">College</p>
                      <p className="font-bold">{athlete.college}</p>
                    </div>
                  </div>
                )}
              </div>

              {athlete.draft_year && (
                <div className="flex items-center gap-3 p-4 rounded-lg bg-primary/5 border border-primary/10">
                  <Trophy className="h-6 w-6 text-primary" />
                  <div>
                    <p className="text-xs text-muted-foreground">Draft Information</p>
                    <p className="font-bold">
                      {athlete.draft_year}
                      {athlete.draft_round && ` • Round ${athlete.draft_round}`}
                      {athlete.draft_number && ` • Pick ${athlete.draft_number}`}
                    </p>
                  </div>
                </div>
              )}

              {!athlete.height && !athlete.weight && !athlete.years_experience && !athlete.college && !athlete.draft_year && (
                <p className="text-muted-foreground text-center py-8">No athletic information available</p>
              )}
            </TabsContent>

            <TabsContent value="stats" className="mt-4 space-y-4">
              {athlete.current_season_stats && Object.keys(athlete.current_season_stats).length > 0 ? (
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                    <Award className="h-4 w-4" />
                    Current Season
                  </h4>
                  <div className="grid grid-cols-3 gap-3">
                    {Object.entries(athlete.current_season_stats).slice(0, 9).map(([key, value]) => (
                      <div key={key} className="p-3 rounded-lg bg-muted/50 text-center">
                        <p className="text-xs text-muted-foreground uppercase">{key.replace(/_/g, ' ')}</p>
                        <p className="font-bold text-lg">{String(value)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {athlete.career_stats && Object.keys(athlete.career_stats).length > 0 ? (
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                    <Users2 className="h-4 w-4" />
                    Career Stats
                  </h4>
                  <div className="grid grid-cols-3 gap-3">
                    {Object.entries(athlete.career_stats).slice(0, 9).map(([key, value]) => (
                      <div key={key} className="p-3 rounded-lg bg-muted/50 text-center">
                        <p className="text-xs text-muted-foreground uppercase">{key.replace(/_/g, ' ')}</p>
                        <p className="font-bold text-lg">{String(value)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {(!athlete.current_season_stats || Object.keys(athlete.current_season_stats).length === 0) &&
               (!athlete.career_stats || Object.keys(athlete.career_stats).length === 0) && (
                <p className="text-muted-foreground text-center py-8">No statistics available</p>
              )}
            </TabsContent>

            <TabsContent value="buzz" className="mt-4">
              <AthleteBuzzTab
                athleteId={athlete.id}
                athleteName={athlete.display_name || athlete.name}
                teamName={athlete.current_team_name || undefined}
                sport={athlete.sport}
              />
            </TabsContent>
          </Tabs>

          {/* Action buttons */}
          <div className="px-6 pb-6 flex gap-3">
            {athlete.claim_status === 'approved' ? (
              <Button 
                className="flex-1" 
                variant="default"
                onClick={() => {
                  if (athlete && onInviteToDeal) {
                    onInviteToDeal(athlete);
                    onOpenChange(false);
                  }
                }}
              >
                <Mail className="h-4 w-4 mr-2" />
                Invite to Deal
              </Button>
            ) : (
              <Button 
                className="flex-1" 
                variant="default"
                onClick={() => setInviteModalOpen(true)}
              >
                <Users2 className="h-4 w-4 mr-2" />
                Invite to Platform
              </Button>
            )}
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        </ScrollArea>
      </DialogContent>

      {athlete && (
        <InviteToPlatformModal
          open={inviteModalOpen}
          onOpenChange={setInviteModalOpen}
          athleteName={athlete.display_name || athlete.name}
          athleteSport={athlete.sport}
          athleteTeam={athlete.current_team_name || undefined}
        />
      )}
    </Dialog>
  );
};

export default MarketplaceAthleteDetailModal;
